import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getCharacters, createCharacter, updateCharacter, deleteCharacter, extractErrorMessage } from '../services/api';
import { FaPlus, FaEdit, FaTrash, FaUser } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Characters.css';

function Characters({ user }) {
  const toast = useToast();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingChar, setEditingChar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    first_name: '',
    last_name: '',
    race: '',
    clan: '',
    gender: '',
    job: '',
    role: 'main',
    server: '',
    data_center: '',
    description: '',
    avatar_url: ''
  });

  useEffect(() => {
    fetchCharacters();
  }, []);

  const fetchCharacters = async () => {
    try {
      setLoading(true);
      const data = await getCharacters();
      setCharacters(data.characters || data);
    } catch (error) {
      console.error('Error fetching characters:', error);
      toast.error(extractErrorMessage(error, 'Charaktere konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', first_name: '', last_name: '', race: '', clan: '', gender: '',
      job: '', role: 'main', server: '', data_center: '', description: '', avatar_url: ''
    });
    setEditingChar(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (char) => {
    setEditingChar(char);
    setFormData({
      name: char.name || '',
      first_name: char.first_name || '',
      last_name: char.last_name || '',
      race: char.race || '',
      clan: char.clan || '',
      gender: char.gender || '',
      job: char.job || '',
      role: char.role || 'main',
      server: char.server || '',
      data_center: char.data_center || '',
      description: char.description || '',
      avatar_url: char.avatar_url || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      if (editingChar) {
        await updateCharacter(editingChar.id, formData);
      } else {
        await createCharacter(formData);
      }
      setShowModal(false);
      resetForm();
      toast.success(editingChar ? 'Charakter aktualisiert' : 'Charakter erstellt');
      fetchCharacters();
    } catch (error) {
      console.error('Error saving character:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern des Charakters'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Charakter wirklich löschen?')) return;
    try {
      await deleteCharacter(id);
      toast.success('Charakter gelöscht');
      fetchCharacters();
    } catch (error) {
      console.error('Error deleting character:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Charakters'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getRoleBadge = (role) => {
    const badges = {
      main: { class: 'main', label: 'Main' },
      alt: { class: 'alt', label: 'Alt' },
      rp: { class: 'rp', label: 'RP' },
      npc: { class: 'npc', label: 'NPC' }
    };
    return badges[role] || badges.main;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Charaktere</h1>
          <button className="add-btn" onClick={handleOpenCreate}>
            <FaPlus /> Neuer Charakter
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="characters-grid">
            {characters.length > 0 ? (
              characters.map(char => {
                const roleBadge = getRoleBadge(char.role);
                return (
                  <div key={char.id} className="character-card">
                    <div className="character-avatar">
                      {char.avatar_url ? (
                        <img src={char.avatar_url} alt={char.name || `${char.first_name} ${char.last_name}`} />
                      ) : (
                        <div className="avatar-placeholder"><FaUser /></div>
                      )}
                    </div>
                    <div className="character-info">
                      <div className="character-name-row">
                        <h3 className="character-name">
                          {char.name || `${char.first_name || ''} ${char.last_name || ''}`.trim()}
                        </h3>
                        <span className={`role-badge ${roleBadge.class}`}>{roleBadge.label}</span>
                      </div>
                      <div className="character-details">
                        {char.race && <span className="detail-item">{char.race}{char.clan ? ` - ${char.clan}` : ''}</span>}
                        {char.job && <span className="detail-item job">{char.job}</span>}
                        {char.server && (
                          <span className="detail-item server">
                            {char.server}{char.data_center ? ` (${char.data_center})` : ''}
                          </span>
                        )}
                      </div>
                      {char.description && <p className="character-desc">{char.description}</p>}
                      <div className="character-actions">
                        <button className="action-btn edit" onClick={() => handleOpenEdit(char)} title="Bearbeiten">
                          <FaEdit />
                        </button>
                        {user.isAdmin && (
                          <button className="action-btn delete" onClick={() => handleDelete(char.id)} title="Löschen">
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-data">Keine Charaktere vorhanden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal modal-wide" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingChar ? 'Charakter bearbeiten' : 'Neuer Charakter'}</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Vorname</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={e => setFormData({...formData, first_name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Nachname</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={e => setFormData({...formData, last_name: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Anzeigename</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="Optional - überschreibt Vor-/Nachname"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Volk</label>
                    <select
                      value={formData.race}
                      onChange={e => setFormData({...formData, race: e.target.value})}
                    >
                      <option value="">Auswählen...</option>
                      <option value="Hyur">Hyur</option>
                      <option value="Miqo'te">Miqo'te</option>
                      <option value="Lalafell">Lalafell</option>
                      <option value="Roegadyn">Roegadyn</option>
                      <option value="Elezen">Elezen</option>
                      <option value="Au Ra">Au Ra</option>
                      <option value="Viera">Viera</option>
                      <option value="Hrothgar">Hrothgar</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Clan</label>
                    <input
                      type="text"
                      value={formData.clan}
                      onChange={e => setFormData({...formData, clan: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Geschlecht</label>
                    <select
                      value={formData.gender}
                      onChange={e => setFormData({...formData, gender: e.target.value})}
                    >
                      <option value="">Auswählen...</option>
                      <option value="male">Männlich</option>
                      <option value="female">Weiblich</option>
                      <option value="other">Andere</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Job/Klasse</label>
                    <input
                      type="text"
                      value={formData.job}
                      onChange={e => setFormData({...formData, job: e.target.value})}
                      placeholder="z.B. Paladin, White Mage..."
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rolle</label>
                    <select
                      value={formData.role}
                      onChange={e => setFormData({...formData, role: e.target.value})}
                    >
                      <option value="main">Main</option>
                      <option value="alt">Alt</option>
                      <option value="rp">RP</option>
                      <option value="npc">NPC</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Server</label>
                    <input
                      type="text"
                      value={formData.server}
                      onChange={e => setFormData({...formData, server: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Data Center</label>
                  <input
                    type="text"
                    value={formData.data_center}
                    onChange={e => setFormData({...formData, data_center: e.target.value})}
                    placeholder="z.B. Light, Chaos..."
                  />
                </div>
                <div className="form-group">
                  <label>Avatar-URL</label>
                  <input
                    type="text"
                    value={formData.avatar_url}
                    onChange={e => setFormData({...formData, avatar_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    placeholder="Charakterbeschreibung..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert\u2026' : (editingChar ? 'Speichern' : 'Erstellen')}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Characters;
